// The Board's current plans (today's board/PlanStrip.tsx): the selected
// project's plans In Progress, then Planned; choosing one narrows the board to
// the issues it lists (`?plan=`), choosing it again shows the whole board.
// Labels on plans are left out.
import { memo, useMemo } from 'react'
import { Link } from 'react-router-dom'
import type { Issue, PlanRow } from '../api/types'
import { LinkIcon, PlanStatusIcon } from '../components/Icons'
import { planPath, projectPath } from '../projects/routing'
import { useBoardState, useSelectedProject } from '../store/context'
import { cx } from '../transcript/bits'
import { CURRENT_PLAN_STATUSES, planStatusName } from '../vocab'
import { ProgressBar } from './ProgressBar'
import { comparePlans, planProgress } from './state'
import { stripWords as w } from './words'
import './plans.css'

export function PlanStrip({ selected, onToggle }: { selected: string | null; onToggle: (planId: string) => void }) {
  const { plans, issues } = useBoardState()
  const project = useSelectedProject()
  const current = useMemo(() => {
    const rank = (p: PlanRow) => CURRENT_PLAN_STATUSES.indexOf(p.status)
    return [...plans.values()].filter((p) => p.project === project?.id && rank(p) >= 0).sort((a, b) => rank(a) - rank(b) || comparePlans(a, b))
  }, [plans, project?.id])
  if (!project || current.length === 0) return null
  return (
    <section className="plan-strip" aria-label={w.current}>
      {current.map((plan) => (
        <PlanChip key={plan.id} plan={plan} projectKey={project.key} selected={plan.id === selected} onToggle={onToggle} issues={issues} />
      ))}
      <Link to={projectPath(project.key, '/plans')} className="plan-strip-all">
        {w.all}
      </Link>
    </section>
  )
}

const PlanChip = memo(function PlanChip({ plan, projectKey, selected, onToggle, issues }: { plan: PlanRow; projectKey: string; selected: boolean; onToggle: (planId: string) => void; issues: ReadonlyMap<string, Issue> }) {
  const progress = planProgress(plan, issues)
  return (
    <div className={cx('plan-chip', selected && 'is-selected')}>
      <button
        type="button"
        className="plan-chip-main"
        aria-pressed={selected}
        aria-label={w.chip(plan.title, planStatusName(plan.status), progress.done, progress.total)}
        onClick={() => onToggle(plan.id)}
        title={selected ? w.whole : w.only}
      >
        <span className="plan-chip-head">
          <PlanStatusIcon status={plan.status} />
          <span className="plan-chip-title">{plan.title}</span>
        </span>
        {plan.summary && <span className="plan-chip-summary">{plan.summary}</span>}
        <span className="plan-chip-meta">
          <ProgressBar progress={progress} label={w.listed} />
        </span>
      </button>
      <Link className="plan-chip-open" to={planPath(projectKey, plan.id)} title={w.open} aria-label={w.openNamed(plan.title)}>
        <LinkIcon size={14} />
      </Link>
    </div>
  )
})
